"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const R = __importStar(require("ramda"));
const utils_1 = require("@8base/utils");
const decimal_js_1 = require("decimal.js");
const validator_constants_1 = require("./validator.constants");
const INT_MAX_VALUE = Math.pow(2, 31) - 1;
const INT_MIN_VALUE = -Math.pow(2, 31);
const FLOAT_MAX_VALUE = Number.MAX_VALUE;
const FLOAT_MIN_VALUE = -Number.MAX_VALUE;
const getFieldTypeAttributes = R.prop('fieldTypeAttributes');
const getMinValueFromAttributes = R.pipe(getFieldTypeAttributes, R.prop('minValue'));
const getMaxValueFromAttributes = R.pipe(getFieldTypeAttributes, R.prop('maxValue'));
const getMaxPrecisionFromAttributes = R.pipe(getFieldTypeAttributes, R.prop('precision'));
const isBigIntFromAttributes = R.pipe(getFieldTypeAttributes, R.prop('isBigInt'));
const getMaxFieldSizeFromAttributes = R.pipe(getFieldTypeAttributes, R.prop('fieldSize'));
const getFormatFromAttributes = R.pipe(getFieldTypeAttributes, R.prop('format'));
const getMinValue = (field) => {
    const minValue = getMinValueFromAttributes(field);
    const precision = getMaxPrecisionFromAttributes(field);
    if (typeof minValue === 'number') {
        return minValue;
    }
    else if (typeof precision === 'number' && precision !== 0) {
        return FLOAT_MIN_VALUE;
    }
    return INT_MIN_VALUE;
};
const getMaxValue = (field) => {
    const maxValue = getMaxValueFromAttributes(field);
    const precision = getMaxPrecisionFromAttributes(field);
    if (typeof maxValue === 'number') {
        return maxValue;
    }
    else if (typeof precision === 'number' && precision !== 0) {
        return FLOAT_MAX_VALUE;
    }
    return INT_MAX_VALUE;
};
const isEmpty = R.either(R.isNil, R.isEmpty);
const checkRequired = R.ifElse(R.complement(isEmpty), R.always(undefined), R.always(validator_constants_1.VALIDATION_ERROR.IS_REQUIRED()));
const checkIsNumber = R.ifElse(R.pipe(R.cond([[R.isNil, R.always(0)], [R.T, Number]]), R.complement(Number.isNaN)), R.always(undefined), R.always(validator_constants_1.VALIDATION_ERROR.NOT_A_NUMBER()));
const checkIsJson = value => {
    if (typeof value === 'string' && value.length > 0) {
        try {
            JSON.parse(value);
            return undefined;
        }
        catch (e) {
            return validator_constants_1.VALIDATION_ERROR.NOT_A_JSON(e.message);
        }
    }
};
// TODO: replace ternary operator by R.ifElse
// when https://github.com/flowtype/flow-typed/issues/2411
// will be resolved.
const checkMaxPrecision = maxPrecision => (value) => {
    if (isEmpty(value)) {
        return undefined;
    }
    decimal_js_1.Decimal.set({ defaults: true });
    const decimalValue = new decimal_js_1.Decimal(value);
    const precision = decimalValue.decimalPlaces();
    return precision <= maxPrecision ? undefined : validator_constants_1.VALIDATION_ERROR.MAX_PRECISION(maxPrecision);
};
const checkMinValue = minValue => R.ifElse(R.cond([
    [isEmpty, R.T],
    [
        R.T,
        R.pipe(Number, R.lte(minValue)),
    ],
]), R.always(undefined), R.always(validator_constants_1.VALIDATION_ERROR.MIN_VALUE(minValue)));
const checkMaxValue = maxValue => R.ifElse(R.cond([
    [isEmpty, R.T],
    [
        R.T,
        R.pipe(Number, R.gte(maxValue)),
    ],
]), R.always(undefined), R.always(validator_constants_1.VALIDATION_ERROR.MAX_VALUE(maxValue)));
const checkMaxFieldSize = maxFieldSize => R.ifElse(R.pipe(R.propOr(0, 'length'), R.gte(maxFieldSize)), R.always(undefined), R.always(validator_constants_1.VALIDATION_ERROR.MAX_FIELD_SIZE(maxFieldSize)));
// TODO: replace ternary operator by R.ifElse
// when https://github.com/flowtype/flow-typed/issues/2411
// will be resolved.
const checkFormat = (format) => (value) => {
    if (isEmpty(value)) {
        return undefined;
    }
    return R.test(validator_constants_1.FORMAT_PATTERN[format], value || '') ? undefined : validator_constants_1.VALIDATION_ERROR[format]();
};
const getCommonValidators = field => {
    const validators = [];
    if (field.isRequired) {
        validators.push(checkRequired);
    }
    return validators;
};
const getNumberFieldValidators = field => {
    const validators = [checkIsNumber];
    const maxPrecision = getMaxPrecisionFromAttributes(field);
    const minValue = getMinValue(field);
    const maxValue = getMaxValue(field);
    const isBigInt = isBigIntFromAttributes(field);
    if (maxPrecision || maxPrecision === 0) {
        validators.push(checkMaxPrecision(maxPrecision));
    }
    if (minValue && !isBigInt) {
        validators.push(checkMinValue(minValue));
    }
    if (maxValue && !isBigInt) {
        validators.push(checkMaxValue(maxValue));
    }
    return validators;
};
const getTextFieldValidators = field => {
    const validators = [];
    const maxFieldSize = getMaxFieldSizeFromAttributes(field);
    const format = getFormatFromAttributes(field);
    if (maxFieldSize) {
        validators.push(checkMaxFieldSize(maxFieldSize));
    }
    if (format && validator_constants_1.FORMAT_PATTERN[format]) {
        validators.push(checkFormat(format));
    }
    return validators;
};
const getDateFieldValidators = field => {
    const validators = [];
    const format = getFormatFromAttributes(field);
    if (format && validator_constants_1.FORMAT_PATTERN[format]) {
        validators.push(checkFormat(format));
    }
    return validators;
};
const getSwitchFieldValidators = () => [];
const getFileFieldValidators = () => [];
const getRelationFieldValidators = () => [];
const getSmartFieldValidators = () => [];
const getJSONFieldValidators = () => [checkIsJson];
const processValidators = validators => R.converge((...values) => R.find(R.is(String))(values), validators);
/**
 * ValidatorFacade creates validaton function based on field metadata
 * @param field - Field metadata
 * @returns - Validation function
 */
exports.validatorFacade = R.pipe(R.converge(R.concat, [
    getCommonValidators,
    R.cond([
        [R.propEq('fieldType', utils_1.FIELD_TYPE.NUMBER), getNumberFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.TEXT), getTextFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.DATE), getDateFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.SWITCH), getSwitchFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.FILE), getFileFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.RELATION), getRelationFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.SMART), getSmartFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.JSON), getJSONFieldValidators],
        [R.propEq('fieldType', utils_1.FIELD_TYPE.GEO), () => [() => undefined]],
        [
            R.T,
            field => {
                throw new utils_1.SDKError(utils_1.ERROR_CODES.UNSUPPORTED_FIELD_TYPE, utils_1.PACKAGES.VALIDATE, `Validator doesn't support field type ${field.fieldType}`);
            },
        ],
    ]),
]), processValidators);
//# sourceMappingURL=validator.js.map